<template>
<div class="page-recherche-avancee page-recherche-avancee-substances">
  <main class="main">
    <breadcrumbs></breadcrumbs>

    <div class="page_title">
      <h1>Recherche avancée substances actives</h1>
    </div>

    <transition
      :duration="5"
      name="slide-fade"
      mode="out-in"
    >
      <div
        v-if="toggleSearch"
        class="main-content"
        key="form"
      >
        <form
          v-on:submit.prevent
          v-on:keydown.enter.prevent
          ref="form"
        >
          <h2>Identité</h2>
          <identity-form
            v-bind.sync="searchInput"
            :types="types"
            :subtypes="subtypes"
            :families="families"
            :activities="activities"
          >
          </identity-form>

          <h2>Propriétés / mode d'action</h2>
          <property-form
            v-bind.sync="searchInput"
            :solubility_water_20Operator.sync="searchInputWithOperator.solubility_water_20Operator"
            :solubility_water_20.sync="searchInputWithOperator.solubility_water_20"
            :djaOperator.sync="searchInputWithOperator.djaOperator"
            :dja.sync="searchInputWithOperator.dja"
            :pnecOperator.sync="searchInputWithOperator.pnecOperator"
            :pnec.sync="searchInputWithOperator.pnec"
            :ractypesRef="ractypes"
            :insecticidePlantBehaviorRef="insecticidePlantBehavior"
            :herbicideMigrationModeRef="herbicideMigrationMode"
            @update-search-array="updateSearchArray($event)"
          ></property-form>

          <h2>Usages</h2>
          <uses-form
            :cultivations.sync="searchInput.cultivations"
            :targets.sync="searchInput.targets"
            :cultivationsRef="cultivations"
            :targetsRef="targets"
          ></uses-form>

          <classification-form
            :classificationsList="classifications"
            :classifications.sync="searchInputWithClauseType.classifications"
            v-bind.sync="searchInput"
          ></classification-form>

          <div class="col-100 recherche-avancee-substances-footer">
            <button
              @click="clearSearch"
              type="button"
              class="grey-background"
            >
              Effacer la recherche
            </button>
            <button
              @click="search(); scrollToTop()"
              class="orange-background"
            >
              Rechercher
            </button>
          </div>
        </form>
      </div>
      <div
        v-else
        class="main-content"
        key="result"
      >
        <advanced-search-modify-save-bar
          @modify-search="modifySearch()"
          :type="'substance'"
        ></advanced-search-modify-save-bar>

        <advanced-search-facets
          :facets="facets"
          @updateFacets="updateFacets($event)"
          @showMoreFacet="queryParams[`${$event.key}Number`] = $event.number; updateUrl()"
        ></advanced-search-facets>

        <substances-listing
          :activeSubstances="substances"
          :routeNameToDetail="'substanceIdentityAdvancedSearch'"
          v-bind.sync="queryParams"
          @changePage="updateUrl()"
        ></substances-listing>
      </div>
    </transition>
  </main>
</div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs'
import SubstancesListing from '@/components/SubstancesListing'
import ClassificationForm from '@/components/ClassificationForm'
import UsesForm from '@/views/SubstancesAdvancedSearch/components/UsesForm'
import IdentityForm from '@/views/SubstancesAdvancedSearch/components/IdentityForm'
import PropertyForm from '@/views/SubstancesAdvancedSearch/components/PropertyForm'
import AdvancedSearchFacets from '@/components/AdvancedSearchFacets'
import AdvancedSearchModifySaveBar from '@/components/AdvancedSearchModifySaveBar'
import SearchMixin from '@/mixins/SearchMixin.js'
import AdvancedSearchMixin from '@/mixins/AdvancedSearchMixin.js'
import Substance from '@/api/substances.js'

export default {
  name: 'SubstancesAdvancedSearch',
  components: {
    Breadcrumbs,
    IdentityForm,
    SubstancesListing,
    PropertyForm,
    ClassificationForm,
    AdvancedSearchFacets,
    UsesForm,
    AdvancedSearchModifySaveBar
  },
  mixins: [SearchMixin, AdvancedSearchMixin],

  data () {
    return {
      toggleSearch: !this.$route.params.results,
      errors: {
        page: null
      },
      substances: {},
      queryParams: {
        page: 1
      },
      searchInput: {
        // identity
        name: '',
        cas_number: '',
        types_select: '',
        subtypes_select: '',
        principal_activity: '',
        family: '',
        inscription_date_start: '',
        inscription_date_expiration: '',
        inscription_date_expirationOperator: '',

        // property
        code_rac: '',
        rac_type: '',
        insecticide_plant_behavior: '',
        insecticide_penetration: [''],
        fungicide_plant_behavior: [''],
        fungicide_mushroom_behavior: [''],
        herbicide_penetration: [''],
        herbicide_migration: '',
        cmr: false,
        

        // usages
        cultivations: [''],
        targets: ['']
      },

      searchInputWithOperator: {
        solubility_water_20Operator: '',
        solubility_water_20: '',
        djaOperator: '',
        dja: '',
        pnecOperator: '',
        pnec: ''
      },

      searchInputWithClauseType: {
        classifications: []
      },

      types: [],
      subtypes: [],
      families: [],
      activities: [],
      ractypes: [],
      insecticidePlantBehavior: [],
      insecticidePenetrationMode: [],
      fungicidePlantBehavior: [],
      fungicideMushroomBehavior: [],
      herbicidePenetrationMode: [],
      herbicideMigrationMode: [],
      cultivations: [],
      targets: [],
      classifications: {},
      facets: {
        types: {
          title: 'Types',
          number: 3,
          availables: [],
          checked: [],
          count: null
        },
        targets: {
          title: 'Cibles',
          number: 3,
          availables: [],
          checked: [],
          count: null
        },
        cultivations: {
          title: 'Cultures',
          number: 3,
          availables: [],
          checked: [],
          count: null
        }
      }
    }
  },

  created () {
    Promise.all([
      this.getTypes(),
      this.getSubTypes(),
      this.getFamilies(),
      this.getActivities(),
      this.getRactypes(),
      this.getInsecticidePlantBehavior(),
      this.getHerbicideMigrationMode(),
      this.getCultivations(),
      this.getTargets(),
      this.getClassifications()
    ])
      .then(() => {
        this.putQueryParamsInSearchInput()
        this.putQueryParamsInSearchInputWithOperator()
        this.putQueryParamsInSearchInputWithClauseType()
        this.putQueryParamsInFacets()
      })
    this.queryParams = { ...this.queryParams, ...this.$route.query }
    this.handleMultiValuatedQueryParam([
      'insecticide_penetration', 'fungicide_plant_behavior', 'classifications',
      'fungicide_mushroom_behavior', 'herbicide_penetration',
      ...Object.keys(this.facets).map(key => `${key}Facet`)
    ])
    if (!this.toggleSearch) this.getSubstances()
  },

  beforeRouteUpdate (to, from, next) {
    this.queryParams = { ...to.query }
    this.toggleSearch = !to.params.results
    this.handleMultiValuatedQueryParam([
      'insecticide_penetration', 'fungicide_plant_behavior', 'classifications',
      'fungicide_mushroom_behavior', 'herbicide_penetration', 'cultivations',
      'targets', ...Object.keys(this.facets).map(key => `${key}Facet`)
    ])
    this.putQueryParamsInSearchInput()
    this.putQueryParamsInSearchInputWithOperator()
    this.putQueryParamsInSearchInputWithClauseType()
    this.putQueryParamsInFacets()
    if (!this.toggleSearch) {
      Promise.resolve(this.getSubstances())
        .finally(() => next())
    } else {
      next()
    }
  },

  methods: {
    // referentials
    getTypes () {
      return Substance.types().then(
        response => { this.types = response })
    },

    getSubTypes () {
      return Substance.subtypes().then(
        response => { this.subtypes = response })
    },

    getFamilies () {
      return Substance.families().then(
        response => { this.families = response })
    },

    getActivities () {
      return Substance.activities().then(
        response => { this.activities = response })
    },

    getRactypes () {
      return Substance.ractypes().then(
        response => { this.ractypes = response })
    },

    getInsecticidePlantBehavior () {
      return Substance.insecticidePlantBehavior().then(
        response => { this.insecticidePlantBehavior = response })
    },

    getHerbicideMigrationMode () {
      return Substance.herbicideMigrationMode().then(
        response => { this.herbicideMigrationMode = response })
    },

    getCultivations () {
      return Substance.cultivations()
        .then(response => { this.cultivations = response })
    },

    getTargets () {
      return Substance.targets()
        .then(response => { this.targets = response })
    },

    getClassifications () {
      return Substance.classifications()
        .then(response => {
          this.classifications = response
          for (let i = 0; i < Object.keys(response).length; i++) {
            this.$set(this.searchInputWithClauseType.classifications, i, [])
          }
        })
    },

    getSubstances () {
      this.queryParams.page = Number(this.queryParams.page) || 1
      return Substance.list({ ...this.queryParams, ...{ advancedSearch: true } })
        .then(response => {
          this.substances = response.substances
          for (const prop in response.facets) {
            if (prop === 'firms') continue // exclude firm facet
            this.facets[prop] = { ...this.facets[prop], ...response.facets[prop] }
          }
        })
    },

    updateSearchArray (event) {
      this.searchInput[event.name] = event.checked ? (
        (this.searchInput[event.name].length && this.searchInput[event.name][0] === '')
          // if the updated array only includes an empty string
          ? [...this.searchInput[event.name].fill(event.value, 0, 1)]
          : this.searchInput[event.name].concat(event.value)
      ) : this.searchInput[event.name].filter(x => x !== event.value)
    }
  }
}
</script>
